<mat-form-field
  [appearance]="appearance"
  [class]="fieldClass"
  [dir]="direction"
  [floatLabel]="resolvedFloatLabel"
  (click)="onMatFormFieldClick($event)"
  >
  @if (label) {
    <mat-label [class]="labelClass + ' text-sm'">{{
      label
    }}</mat-label>
  }
  @if (prefixIcon) {
    <ng-icon
      matPrefix
      [dir]="direction"
      [name]="prefixIcon"
      [class]="prefixIconClass"
    ></ng-icon>
  }

  @if (readOnly) {
    <!--
      Read-only trigger: dialog-opening button, NOT a combobox.
      Rendered without [matAutocomplete] so MatAutocompleteTrigger's host bindings
      (which force role=combobox / aria-haspopup=listbox) never apply.
      Click bubbles to mat-form-field which calls onMatFormFieldClick.
      Keyboard activation (Enter/Space) is handled via (keydown).

      a11y: do NOT set the native `readonly` attribute. Audit reported SR (NVDA)
      announcing "לקריאה בלבד / read-only" on this trigger, which contradicts the
      reality that activating it lets the user fill the field (via the dialog).
      With role="button" the ARIA-in-HTML mapping ignores readonly, but some SR
      builds still leak the textbox semantics through, so we drop the attribute
      entirely. The input is visually-hidden (no caret), there is no FormControl
      binding on this inner input (parent CVA owns the value), and keydown
      intercepts every non-activation key — so the host form value cannot drift.
    -->
    <input
      #motAutocompleteInput
      matInput
      role="button"
      aria-haspopup="dialog"
      [class]="inputClass"
      [type]="type"
      [attr.tabindex]="inputTabIndex ?? null"
      [dir]="direction"
      [attr.value]="displayValue"
      [placeholder]="placeholder"
      [attr.aria-expanded]="dialogOpen ? 'true' : 'false'"
      [attr.aria-label]="readOnlyAriaLabel"
      (keydown)="onAutocompleteInputKeydown($event)"
      [ngStyle]="{'text-align': direction === 'rtl' ? 'right' : 'left'}"
      />
  } @else {
    <!--
      a11y: property binding [aria-describedby] (not [attr.]) so MatInput's @Input fires;
      MatFormField then keeps the error id in its merged aria-describedby on the host.
    -->
    <input
      #motAutocompleteInput
      matInput
      role="combobox"
      aria-autocomplete="list"
      aria-haspopup="listbox"
      [class]="inputClass"
      [type]="type"
      [attr.tabindex]="inputTabIndex ?? null"
      [dir]="direction"
      [placeholder]="placeholder"
      [matAutocomplete]="auto"
      [attr.aria-label]="label || null"
      [attr.aria-expanded]="isAutocompletePanelOpen ? 'true' : 'false'"
      [attr.aria-controls]="isAutocompletePanelOpen ? autocompletePanelId : null"
      [aria-describedby]="(formControl?.invalid && formControl?.touched && displayErrors) ? errorId : null"
      [attr.aria-invalid]="(formControl?.invalid && formControl?.touched) ? 'true' : null"
      (input)="onInput($event)"
      (keydown)="onEditableAutocompleteKeydown($event)"
      [ngStyle]="{'text-align': direction === 'rtl' ? 'right' : 'left'}"
      />
  }

    @if (inputIcon) {
      <button
        matSuffix
        type="button"
        class="!min-w-0 !p-0 !border-0 !bg-transparent cursor-pointer"
        [attr.aria-label]="iconButtonAriaLabel"
        (click)="$event.stopPropagation(); onIconClick.emit($event)">
        <ng-icon
          [dir]="direction"
          [name]="inputIcon"
          [class]="inputIconClass + ' z-5'"
        ></ng-icon>
      </button>
    }

    @if (!!hasTrigger) {
      <ng-content select="[trigger]" />
    }

    <!--
      a11y: on open, activate the first option and move DOM focus into the panel
      so screen readers enter the list; while filtering, focus stays on the input.
    -->
    <mat-autocomplete
      #auto="matAutocomplete"
      [id]="autocompletePanelId"
      [displayWith]="wrappedDisplayWith"
      [autoActiveFirstOption]="true"
      class="use-scrollbar"
      role="listbox"
      [attr.aria-label]="label || null"
      (opened)="onAutocompletePanelOpened()"
      (closed)="onAutocompletePanelClosed()"
    >
      @for (opt of resolvedOptions; track trackOption($index, opt); let i = $index) {
        <mat-option
          [value]="opt.value"
          [disabled]="opt.disabled"
          (onSelectionChange)="handleSelectionChange($event)"
          >
          @if (customOptionTemplate) {
            <ng-container
              *ngTemplateOutlet="customOptionTemplate; context: { $implicit: opt }"
            ></ng-container>
          }
          @if (!customOptionTemplate) {
            {{ opt.label }}
          }
        </mat-option>
      }
    </mat-autocomplete>
  </mat-form-field>

  @if (formControl.invalid && formControl.touched && !!displayErrors) {
    <mat-error
      [id]="errorId"
      class="error"
      role="alert"
      aria-live="assertive"
      style="color: red; text-align: left"
      >
      {{ getFirstError() }}
    </mat-error>
  }
